import { regionlessPath } from "@/features/regions/constants";
import { CrmArea } from "./services/access";

/**
 * URL -> CRM area, keyed by the first one or two path segments.
 *
 * One entry per sidebar page, mirroring CRM_AREAS on the server. Keeping it in
 * one map is what stops the sidebar and the route guard drifting apart: a nav
 * item that hides while its URL still renders is worse than neither, because it
 * looks like the access screen is lying.
 *
 * Every sidebar link is its own area, so a page nested under another's URL
 * (/users/export, /notifications/broadcast, /audit/...) has a two-segment key,
 * which areaForPathname tries before the one-segment key. Pages that are NOT in
 * the sidebar resolve to the page they belong to — /users/:id is All Users,
 * /products is Bundles, /couples-packs is Sessions — exactly as the server does.
 */
export const ROUTE_AREAS: Record<string, CrmArea> = {
  users: "users",
  "users/decorator-links": "users_decorator_links",
  "users/export": "users_export",
  "pending-requests": "users_pending_requests",

  sessions: "sessions",
  "couples-packs": "sessions",

  prescriptions: "prescriptions",

  bundles: "bundles",
  pricing: "bundles",
  "choose-bundle-link": "bundles",
  products: "bundles",

  "payment-pages": "payment_pages",

  professionals: "supply_config_professionals",
  "our-medical-team": "supply_config_professionals",
  teams: "supply_config_teams",
  "managed-events": "supply_config_managed_events",
  "event-types": "supply_config_event_types",

  coupons: "coupons",

  forms: "forms",
  submissions: "forms_submissions",

  notifications: "notifications",
  "notifications/broadcast": "notifications_push",
  emails: "notifications_emails",

  invoices: "invoices",
  "otp-logs": "otp_logs",
  admins: "internal_users",

  // /audit itself only redirects to the first page.
  audit: "audit_duplicate_accounts",
  "audit/duplicate-accounts": "audit_duplicate_accounts",
  "audit/bundle-discrepancy": "audit_bundle_discrepancy",
  "audit/unmapped-sessions": "audit_unmapped_sessions",

  // Regions and feature flags are administrative configuration, not pages of
  // their own, so they ride on Internal Users rather than getting grid rows the
  // care team would have to reason about.
  regions: "internal_users",
};

/**
 * Where to send someone who has no business on the page they asked for, in
 * sidebar order.
 *
 * The router used to redirect to "users" from both its index and its catch-all.
 * With areas enforced that is a redirect loop for any role without Users -> View,
 * so the landing page has to be chosen from what the role can actually open.
 */
export const AREA_LANDING_PATHS: { area: CrmArea; path: string }[] = [
  { area: "users", path: "users" },
  { area: "users_pending_requests", path: "pending-requests" },
  { area: "users_decorator_links", path: "users/decorator-links" },
  { area: "users_export", path: "users/export" },
  { area: "sessions", path: "sessions" },
  { area: "prescriptions", path: "prescriptions" },
  { area: "bundles", path: "bundles" },
  { area: "payment_pages", path: "payment-pages" },
  { area: "supply_config_professionals", path: "professionals" },
  { area: "supply_config_teams", path: "teams" },
  { area: "supply_config_managed_events", path: "managed-events" },
  { area: "supply_config_event_types", path: "event-types" },
  { area: "coupons", path: "coupons" },
  { area: "forms", path: "forms" },
  { area: "forms_submissions", path: "submissions" },
  { area: "notifications", path: "notifications" },
  { area: "notifications_emails", path: "emails" },
  { area: "notifications_push", path: "notifications/broadcast" },
  { area: "invoices", path: "invoices" },
  { area: "otp_logs", path: "otp-logs" },
  { area: "internal_users", path: "admins" },
  { area: "audit_duplicate_accounts", path: "audit/duplicate-accounts" },
  { area: "audit_bundle_discrepancy", path: "audit/bundle-discrepancy" },
  { area: "audit_unmapped_sessions", path: "audit/unmapped-sessions" },
];

/**
 * The area a full CRM pathname belongs to, region segment and all.
 *
 * Pure on purpose: this is the single point every client-side access decision
 * runs through — the route guard and every <HasAccess> gate — so it is the one
 * piece worth testing directly rather than through a rendered tree.
 *
 * Returns undefined for a path with no mapped area, which callers treat as
 * "no area applies" rather than "denied": the server still refuses the request.
 */
export function areaForPathname(pathname: string): CrmArea | undefined {
  const [first, second] = regionlessPath(pathname)
    .replace(/^\//, "")
    .split("/");
  // The more specific key wins: /users/export is Export, /users/abc is All Users.
  return (
    (second ? ROUTE_AREAS[`${first}/${second}`] : undefined) ??
    ROUTE_AREAS[first]
  );
}

/**
 * Pages that sit inside an area but have nothing to edit — they only build or
 * copy links. The "View only" banner would be a lie there, so it is skipped.
 * Access to the page itself is still the area's View grant.
 */
const VIEW_ONLY_NOTICE_EXEMPT = ["/users/decorator-links"];

export function isViewOnlyNoticeExempt(pathname: string): boolean {
  const path = regionlessPath(pathname);
  return VIEW_ONLY_NOTICE_EXEMPT.some((p) => path === p || path.startsWith(p + "/"));
}
